import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '@environments/environment';
import {
    BookingRequest,
    BookingAvailability,
    BookingValidation,
    Booking
} from '@app/_interfaces/booking.interface';
import * as moment from 'moment';

// Interface that matches the backend API response structure
interface BackendApiResponse {
    code: number;
    msg?: string;
    // For available dates endpoint
    availableDates?: string[];
    unavailableDates?: Array<{
        date: string;
        reason?: string;
        remainingQuantity?: number;
        bookedQuantity?: number;
        productQuantity?: number;
        advanceBookingDays?: number;
        minBookingDate?: string;
    }>;
    // For time slot availability endpoint
    selectedDate?: string;
    availableSlots?: Array<{
        id: string;
        startTime: string;
        endTime: string;
        duration: number;
        price: number;
        totalQuantity: number;
        bookedQuantity: number;
        availableQuantity: number;
        isAvailable: boolean;
    }>;
    // Booking configuration
    bookingConfig?: {
        advanceBookingDays?: number;
        advanceBookingHours?: number;
        holdTimeMinutes?: number;
        maxSlotsPerBooking?: number;
        maxBookingsPerUser?: number;
        requireApproval: boolean;
        productQuantity: number;
    };
    // For other endpoints
    booking?: any;
    bookings?: any[];
    stats?: any;
    available?: boolean;
    conflictingBookings?: any[];
    pagination?: {
        currentPage: number;
        totalPages: number;
        totalCount: number;
        limit: number;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        nextPage: number | null;
        previousPage: number | null;
    };
}

// Updated BookingRequest interface to match backend expectations
// Update the EnhancedBookingRequest interface to include time slots
interface EnhancedBookingRequest {
    productId: string;
    companyId: string;
    bookingType: 'period' | 'timeSlots' | 'perDay';
    // For period bookings
    startDate?: string;
    endDate?: string;
    // For per-day bookings
    bookingDates?: string[];
    // For time slot bookings
    selectedDate?: string;
    timeSlots?: Array<{
        startTime: string;
        endTime: string;
        price: number;
        quantity: number;
    }>;
    totalSlots?: number;
    // Common fields
    quantity?: number;
    totalPrice?: number;
    specialRequests?: string;
    customerName?: string;
    customerEmail?: string;
    productName?: {
        en: string;
        ar: string;
    };
    companyName?: {
        en: string;
        ar: string;
    };
}

@Injectable({
    providedIn: 'root'
})
export class BookingService {
    constructor(public http: HttpClient) {} // Made public for component access

    /**
     * Get available time slots for a specific date and product
     * Uses the new /available-slots endpoint
     */
    getAvailableTimeSlots(
        productSlug: string,
        selectedDate: string,
        quantity: number = 1
    ): Observable<BackendApiResponse> {
        return this.http
            .post<BackendApiResponse>(
                `${environment.apiUrl}/bookings/available-slots/${productSlug}`,
                { selectedDate, quantity }
            )
            .pipe(
                map(response => {
                    // Ensure response has expected structure
                    return {
                        code: response.code || 200,
                        msg: response.msg,
                        selectedDate: response.selectedDate,
                        availableSlots: response.availableSlots || [],
                        bookingConfig: response.bookingConfig || {
                            advanceBookingHours: 24,
                            holdTimeMinutes: 0,
                            maxSlotsPerBooking: 5,
                            requireApproval: false,
                            productQuantity: 1
                        }
                    };
                }),
                catchError(error => {
                    return of({
                        code: 400,
                        msg: error.error.msg.includes('109')
                            ? 'please_login_to_view_slots'
                            : 'failed_to_load_time_slots',
                        selectedDate: selectedDate,
                        availableSlots: [],
                        bookingConfig: {
                            advanceBookingHours: 24,
                            holdTimeMinutes: 0,
                            maxSlotsPerBooking: 5,
                            requireApproval: false,
                            productQuantity: 1
                        }
                    });
                })
            );
    }

    /**
     * Check availability for specific booking parameters
     * Matches backend POST /api/bookings/check-availability
     * Updated to use getAvailableTimeSlots for timeSlots booking type
     */
    checkAvailability(
        productId: string,
        bookingType: 'period' | 'timeSlots' | 'perDay',
        startDate: string,
        endDate?: string,
        selectedDate?: string
    ): Observable<BackendApiResponse> {
        // For time slots, use the new dedicated endpoint
        if (bookingType === 'timeSlots' && selectedDate) {
            return this.getAvailableTimeSlots(productId, selectedDate);
        }

        // For other booking types, use the original check-availability endpoint
        const payload: any = {
            productId,
            bookingType,
            startDate
        };

        // Add optional fields based on booking type
        if (bookingType === 'period' && endDate) {
            payload.endDate = endDate;
        }

        return this.http
            .post<BackendApiResponse>(
                `${environment.apiUrl}/bookings/check-availability`,
                payload
            )
            .pipe(
                catchError(error => {
                    return of({
                        code: 400,
                        msg: 'Error checking availability',
                        available: false,
                        conflictingBookings: []
                    });
                })
            );
    }

    /**
     * Get available dates for per-day or period booking
     * Matches backend POST /api/bookings/available-dates/:productSlug
     */
    getAvailableDates(
        productSlug: string,
        startDate: string,
        endDate: string,
        quantity: number = 1
    ): Observable<BackendApiResponse> {
        return this.http
            .post<BackendApiResponse>(
                `${environment.apiUrl}/bookings/available-dates/${productSlug}`,
                {
                    startDate,
                    endDate,
                    quantity
                }
            )
            .pipe(
                map(response => {
                    // Ensure response has expected structure
                    return {
                        code: response.code || 200,
                        msg: response.msg,
                        availableDates: response.availableDates || [],
                        unavailableDates: response.unavailableDates || [],
                        bookingConfig: response.bookingConfig || {
                            advanceBookingDays: 0,
                            maxBookingsPerUser: 0,
                            requireApproval: false,
                            productQuantity: 1
                        }
                    };
                }),
                catchError(error => {
                    return of({
                        code: 400,
                        msg: error.error.msg.includes('109')
                            ? 'please_login_to_view_dates'
                            : 'failed_to_load_available_dates',
                        availableDates: [],
                        unavailableDates: []
                    });
                })
            );
    }

    /**
     * Create new booking
     * Matches backend POST /api/bookings/create
     */
    createBooking(
        bookingData: EnhancedBookingRequest
    ): Observable<BackendApiResponse> {
        // Validate required fields based on booking type
        this.validateBookingData(bookingData);

        // Prepare payload to match backend expectations
        const payload: any = {
            productId: bookingData.productId,
            companyId: bookingData.companyId,
            bookingType: bookingData.bookingType,
            quantity: bookingData.quantity || 1,
            totalPrice: bookingData.totalPrice || 0,
            specialRequests: bookingData.specialRequests || '',
            customerName: bookingData.customerName || '',
            customerEmail: bookingData.customerEmail || '',
            productName: bookingData.productName
                ? bookingData.productName
                : {
                      en: bookingData.productName,
                      ar: bookingData.productName
                  },
            companyName: bookingData.companyName
                ? bookingData.companyName
                : {
                      en: bookingData.companyName,
                      ar: bookingData.companyName
                  }
        };

        // Add type-specific fields
        if (bookingData.bookingType === 'period') {
            if (!bookingData.startDate || !bookingData.endDate) {
                throw new Error(
                    'Start date and end date are required for period bookings'
                );
            }
            payload.startDate = bookingData.startDate;
            payload.endDate = bookingData.endDate;
        }

        if (bookingData.bookingType === 'perDay') {
            if (
                !bookingData.bookingDates ||
                bookingData.bookingDates.length === 0
            ) {
                throw new Error(
                    'Booking dates are required for per-day bookings'
                );
            }
            payload.bookingDates = bookingData.bookingDates;
            // For per-day bookings, also set startDate for backend compatibility
            payload.startDate = bookingData.bookingDates[0];
        }

        if (bookingData.bookingType === 'timeSlots') {
            if (!bookingData.selectedDate) {
                throw new Error(
                    'Selected date is required for time slot bookings'
                );
            }
            if (!bookingData.timeSlots || bookingData.timeSlots.length === 0) {
                throw new Error(
                    'Time slots are required for time slot bookings'
                );
            }

            payload.selectedDate = bookingData.selectedDate;
            payload.startDate = bookingData.selectedDate; // For compatibility
            payload.timeSlots = bookingData.timeSlots;
            payload.totalSlots =
                bookingData.totalSlots || bookingData.timeSlots.length;
        }

        return this.http
            .post<BackendApiResponse>(
                `${environment.apiUrl}/bookings/create`,
                payload
            )
            .pipe(
                catchError(error => {
                    return of({
                        code: 400,
                        msg: error.error?.msg || 'Failed to create booking'
                    });
                })
            );
    }

    /**
     * Get user bookings with pagination and filtering
     * Matches backend GET /api/bookings
     */
    getUserBookings(
        page: number = 1,
        limit: number = 10,
        status?: string
    ): Observable<BackendApiResponse> {
        const params: any = {
            page: page.toString(),
            limit: limit.toString()
        };

        if (status) {
            params.status = status;
        }

        const url = `${environment.apiUrl}/bookings`;

        return this.http
            .get<BackendApiResponse>(url, {
                params
            })
            .pipe(
                catchError(error => {
                    return of({
                        code: 400,
                        msg: 'Failed to load bookings',
                        bookings: []
                    });
                })
            );
    }

    /**
     * Get single booking by booking number
     * Matches backend GET /api/bookings/:bookingNumber
     */
    getBooking(bookingNumber: string): Observable<BackendApiResponse> {
        return this.http
            .get<BackendApiResponse>(
                `${environment.apiUrl}/bookings/${bookingNumber}`
            )
            .pipe(
                catchError(error => {
                    return of({
                        code: 404,
                        msg: 'Booking not found'
                    });
                })
            );
    }

    /**
     * Cancel booking
     * Matches backend POST /api/bookings/cancel/:bookingId
     */
    cancelBooking(
        bookingId: string,
        reason: string = 'Cancelled by user'
    ): Observable<BackendApiResponse> {
        return this.http
            .post<BackendApiResponse>(
                `${environment.apiUrl}/bookings/cancel/${bookingId}`,
                { reason }
            )
            .pipe(
                catchError(error => {
                    return of({
                        code: 400,
                        msg: 'Failed to cancel booking'
                    });
                })
            );
    }

    /**
     * Get booking statistics for user
     * Matches backend GET /api/bookings/stats/user
     */
    getBookingStats(): Observable<BackendApiResponse> {
        return this.http
            .get<BackendApiResponse>(
                `${environment.apiUrl}/bookings/stats/user`
            )
            .pipe(
                catchError(error => {
                    return of({
                        code: 400,
                        msg: 'Failed to load booking statistics',
                        stats: {}
                    });
                })
            );
    }

    /**
     * Validate booking data before sending to backend
     */
    private validateBookingData(bookingData: EnhancedBookingRequest): void {
        // Basic validation
        if (!bookingData.productId) {
            throw new Error('Product ID is required');
        }

        if (!bookingData.companyId) {
            throw new Error('Company ID is required');
        }

        if (!bookingData.bookingType) {
            throw new Error('Booking type is required');
        }

        // Type-specific validation
        if (bookingData.bookingType === 'period') {
            if (!bookingData.startDate || !bookingData.endDate) {
                throw new Error(
                    'Start date and end date are required for period bookings'
                );
            }

            // Validate date format and order
            const start = moment(bookingData.startDate);
            const end = moment(bookingData.endDate);

            if (!start.isValid() || !end.isValid()) {
                throw new Error('Invalid date format');
            }

            if (end.isBefore(start)) {
                throw new Error('End date must be after start date');
            }
        }

        if (bookingData.bookingType === 'perDay') {
            if (
                !bookingData.bookingDates ||
                bookingData.bookingDates.length === 0
            ) {
                throw new Error(
                    'At least one booking date is required for per-day bookings'
                );
            }

            // Validate each date
            bookingData.bookingDates.forEach(date => {
                if (!moment(date).isValid()) {
                    throw new Error(`Invalid date format: ${date}`);
                }
            });
        }

        if (bookingData.bookingType === 'timeSlots') {
            if (!bookingData.selectedDate) {
                throw new Error(
                    'Selected date is required for time slot bookings'
                );
            }

            if (!moment(bookingData.selectedDate).isValid()) {
                throw new Error('Invalid selected date format');
            }

            if (!bookingData.timeSlots || bookingData.timeSlots.length === 0) {
                throw new Error(
                    'At least one time slot is required for time slot bookings'
                );
            }

            // Validate each time slot
            bookingData.timeSlots.forEach((slot, index) => {
                if (!slot.startTime || !slot.endTime) {
                    throw new Error(
                        `Time slot ${index + 1} must have start and end times`
                    );
                }
                if (slot.price < 0) {
                    throw new Error(
                        `Time slot ${index + 1} price cannot be negative`
                    );
                }
                if (slot.quantity < 1) {
                    throw new Error(
                        `Time slot ${index + 1} quantity must be at least 1`
                    );
                }
            });
        }

        // Validate quantity
        if (bookingData.quantity && bookingData.quantity < 1) {
            throw new Error('Quantity must be at least 1');
        }

        // Validate price
        if (bookingData.totalPrice && bookingData.totalPrice < 0) {
            throw new Error('Total price cannot be negative');
        }
    }

    /**
     * Helper method to format dates for API requests
     */
    formatDate(date: Date | string | moment.Moment): string {
        return moment(date).format('YYYY-MM-DD');
    }

    /**
     * Helper method to check if booking type is valid
     */
    isValidBookingType(
        type: string
    ): type is 'period' | 'timeSlots' | 'perDay' {
        return ['period', 'timeSlots', 'perDay'].includes(type);
    }

    /**
     * Helper method to get booking status options
     */
    getBookingStatusOptions(): string[] {
        return [
            'pending',
            'approved',
            'rejected',
            'cancelled',
            'completed',
            'inProgress'
        ];
    }

    /**
     * Validate a single booking item from the cart
     * This method checks if the booking item is still valid/available
     */
    validateBookingItem(
        cartItem: any
    ): Observable<{ valid: boolean; error?: string }> {
        try {
            // Extract booking parameters from cart item
            const params: any = {
                productId: cartItem.product._id,
                bookingType: cartItem.bookingType,
                quantity: cartItem.bookingQuantity || cartItem.amount || 1
            };

            // Add type-specific parameters
            if (cartItem.bookingType === 'period') {
                if (!cartItem.startDate || !cartItem.endDate) {
                    return of({
                        valid: false,
                        error: 'Missing period booking dates'
                    });
                }
                params.startDate = cartItem.startDate;
                params.endDate = cartItem.endDate;
            } else if (cartItem.bookingType === 'perDay') {
                if (
                    !cartItem.bookingDates ||
                    cartItem.bookingDates.length === 0
                ) {
                    return of({
                        valid: false,
                        error: 'Missing per-day booking dates'
                    });
                }
                params.bookingDates = cartItem.bookingDates;
                params.startDate = cartItem.bookingDates[0];
            } else if (cartItem.bookingType === 'timeSlots') {
                

                if (!cartItem.selectedDate && !cartItem.startDate) {
                    return of({
                        valid: false,
                        error: 'Missing selected date for time slot booking'
                    });
                }

                if (!cartItem.timeSlots || cartItem.timeSlots.length === 0) {
                    return of({
                        valid: false,
                        error: 'Missing time slot booking data'
                    });
                }

                // Use selectedDate or fall back to startDate
                params.selectedDate =
                    cartItem.selectedDate || cartItem.startDate;
                params.timeSlots = cartItem.timeSlots;
            }

            // For timeSlots, use the dedicated available-slots endpoint
            if (cartItem.bookingType === 'timeSlots') {
                return this.getAvailableTimeSlots(
                    cartItem.product._id,
                    params.selectedDate,
                    params.quantity
                ).pipe(
                    map((response: BackendApiResponse) => {
                        if (response.code !== 200) {
                            return {
                                valid: false,
                                error:
                                    response.msg ||
                                    'Failed to check time slot availability'
                            };
                        }

                        // Check if all requested slots are still available
                        const availableSlots = response.availableSlots || [];
                        const requestedSlots = cartItem.timeSlots || [];

                        for (const requestedSlot of requestedSlots) {
                            const availableSlot = availableSlots.find(
                                slot =>
                                    slot.startTime ===
                                        requestedSlot.startTime &&
                                    slot.endTime === requestedSlot.endTime
                            );

                            if (!availableSlot) {
                                return {
                                    valid: false,
                                    error: `Time slot ${requestedSlot.startTime}-${requestedSlot.endTime} is no longer available`
                                };
                            }

                            if (!availableSlot.isAvailable) {
                                return {
                                    valid: false,
                                    error: `Time slot ${requestedSlot.startTime}-${requestedSlot.endTime} is no longer available`
                                };
                            }

                            // Check quantity availability
                            const requestedQuantity =
                                requestedSlot.quantity || params.quantity || 1;
                            if (
                                availableSlot.availableQuantity <
                                requestedQuantity
                            ) {
                                return {
                                    valid: false,
                                    error: `Insufficient quantity for time slot ${requestedSlot.startTime}-${requestedSlot.endTime}. Available: ${availableSlot.availableQuantity}, Requested: ${requestedQuantity}`
                                };
                            }
                        }

                        return { valid: true };
                    }),
                    catchError(error => {
                        console.error(
                            'Error checking time slot availability:',
                            error
                        );
                        return of({
                            valid: false,
                            error: 'Failed to validate time slot availability'
                        });
                    })
                );
            }

            // For other booking types, use the regular checkAvailability method
            return this.checkAvailability(
                params.productId,
                params.bookingType,
                params.startDate,
                params.endDate,
                params.selectedDate
            ).pipe(
                map((response: BackendApiResponse) => {
                    if (response.code !== 200) {
                        return {
                            valid: false,
                            error: response.msg || 'Availability check failed'
                        };
                    }

                    // For other booking types, check general availability
                    if (response.available === false) {
                        let errorMsg = 'Booking is no longer available';

                        if (
                            response.conflictingBookings &&
                            response.conflictingBookings.length > 0
                        ) {
                            errorMsg = 'Conflicts with existing bookings';
                        }

                        return { valid: false, error: errorMsg };
                    }

                    // For date-based bookings, check unavailable dates
                    if (
                        response.unavailableDates &&
                        response.unavailableDates.length > 0
                    ) {
                        const requestedDates =
                            cartItem.bookingType === 'perDay'
                                ? cartItem.bookingDates
                                : [cartItem.startDate, cartItem.endDate];

                        const conflictingDates = response.unavailableDates.filter(
                            unavailableDate =>
                                requestedDates.some(
                                    requestedDate =>
                                        moment(requestedDate).format(
                                            'YYYY-MM-DD'
                                        ) === unavailableDate.date
                                )
                        );

                        if (conflictingDates.length > 0) {
                            const firstConflict = conflictingDates[0];
                            let errorMsg = `Date ${firstConflict.date} is no longer available`;

                            if (
                                firstConflict.reason === 'insufficient_quantity'
                            ) {
                                errorMsg += ` (insufficient quantity: ${firstConflict.remainingQuantity}/${firstConflict.productQuantity})`;
                            } else if (
                                firstConflict.reason === 'fully_booked'
                            ) {
                                errorMsg += ' (fully booked)';
                            }

                            return { valid: false, error: errorMsg };
                        }
                    }

                    return { valid: true };
                }),
                catchError(error => {
                    console.error('Error validating booking item:', error);
                    return of({
                        valid: false,
                        error:
                            error?.message ||
                            'Failed to validate booking availability'
                    });
                })
            );
        } catch (error) {
            console.error('Error preparing booking validation:', error);
            return of({
                valid: false,
                error: 'Failed to prepare booking validation'
            });
        }
    }

    /**
     * Validate multiple booking items from the cart
     * This is a convenience method that validates all booking items at once
     */
    validateCartBookingItems(
        cartItems: any[]
    ): Observable<{
        allValid: boolean;
        results: Array<{
            itemId: string;
            productName: string;
            valid: boolean;
            error?: string;
        }>;
    }> {
        const bookingItems = cartItems.filter(item => item.bookingType);

        if (bookingItems.length === 0) {
            return of({ allValid: true, results: [] });
        }

        const validationObservables = bookingItems.map(item =>
            this.validateBookingItem(item).pipe(
                map(result => ({
                    itemId: item.itemId || item._id,
                    productName:
                        item.product?.name?.en ||
                        item.product?.name ||
                        'Unknown Product',
                    valid: result.valid,
                    error: result.error
                }))
            )
        );

        return forkJoin(validationObservables).pipe(
            map(results => ({
                allValid: results.every(result => result.valid),
                results
            })),
            catchError(error => {
                console.error('Error validating cart booking items:', error);
                return of({
                    allValid: false,
                    results: bookingItems.map(item => ({
                        itemId: item.itemId || item._id,
                        productName:
                            item.product?.name?.en ||
                            item.product?.name ||
                            'Unknown Product',
                        valid: false,
                        error: 'Validation failed'
                    }))
                });
            })
        );
    }
}
